import * as Bacon from 'baconjs';
import { DISCOUNTED, ORIGINAL } from './constants.js';

export default class Emitters {
    constructor({ product, pricing, selectors, props, classes, container }) {
        this.product = product;
        this.pricing = pricing;
        this.selectors = selectors;
        this.props = props;
        this.classes = classes;
        this.container = container;

        this.subscriptionActive = this._subscriptionActive;
        this.purchaseType = this._purchaseType;
        this.addToCart = this._addToCart;
        this.sellingPlanId = this._sellingPlanId;
        this.currentVariantId = this._currentVariantId;
        this.priceSubsave = this._variantPriceByType(DISCOUNTED);
        this.priceOnetime = this._variantPriceByType(ORIGINAL);
        this.allPrices = this._allPrices;
    }

    DISCOUNTED = DISCOUNTED;
    ORIGINAL = ORIGINAL;

    get _subscriptionActive() {
        // When the subscribe and save option is active
        const radioSubsave = this.container.querySelector(
            this.selectors.radioSubsave
        );
        const radioOnetime = this.container.querySelector(
            this.selectors.radioOnetime
        );
        const subscriptionOnly = this.product.subscriptionOnly;
        return Bacon.mergeAll(
            [
                radioSubsave
                    ? Bacon.fromEvent(radioSubsave, 'click').map(
                          e => e.target.checked
                      )
                    : null,
                radioOnetime
                    ? Bacon.fromEvent(radioOnetime, 'click').map(
                          e => !e.target.checked
                      )
                    : null,
                Bacon.once(subscriptionOnly || !!radioSubsave.checked)
            ].filter(Boolean)
        );
    }

    get _purchaseType() {
        // Return purchase type
        return this.subscriptionActive.map(active => {
            return active ? this.DISCOUNTED : this.ORIGINAL;
        });
    }

    get _sellingPlanId() {
        // When a user changes the selected selling plan
        const planDropdown = this.container.querySelector(
            this.selectors.plansDropdown
        );

        if (!planDropdown) return;

        return Bacon.mergeAll([
            Bacon.fromEvent(planDropdown, 'change', evt => {
                const select = evt.target;
                return select.options[select.selectedIndex].value;
            }),
            Bacon.once(planDropdown.value)
        ]);
    }

    get _currentVariantId() {
        // Return active variant ID on variant change
        return Bacon.mergeAll([
            Bacon.once(this.product.initialVariantId),
            this._globalVariantEvents,
            this._formVariantEvents
        ]);
    }

    get _allPrices() {
        return Bacon.combineTemplate({
            hasDiscount: this.priceOnetime != this.priceSubsave,
            priceOnetime: this.priceOnetime,
            priceSubsave: this.priceSubsave,
            subscriptionActive: this.subscriptionActive
        });
    }

    get _addToCart() {
        // When a user clicks on the add to cart button
        const addToCartButton = this.container.querySelector(
            this.selectors.addToCart
        );

        if (!addToCartButton) return;

        return Bacon.fromEvent(addToCartButton, 'click', () => {
            const data = new FormData(this.container);
            return Object.fromEntries(data.entries());
        });
    }

    _variantPriceByType(type) {
        return this.currentVariantId
            .map(variantId => {
                return this.product.getPrice(variantId, type);
            })
            .map(price => this.pricing.buildPrice(price));
    }

    get _globalVariantEvents() {
        // Events from custom document event triggers
        if (!this.selectors.globalEventTriggers) return;

        return Bacon.fromEvent(
            document,
            this.selectors.globalEventTriggers
        ).map(() => {
            const variant = this.product.activeVariant;
            return variant.id;
        });
    }

    get _formVariantEvents() {
        // Captures your normal or typical form events
        const variantTriggers = this.container.querySelectorAll(
            this.selectors.variantTrigger || this.selectors.variantOptions
        );
        return [...variantTriggers].map(variantOption => {
            // If select or input, use change event, otherwise use click event to support non-form elemental triggers
            const form =
                variantOption.tagName.toLowerCase() === 'select' ||
                variantOption.tagName.toLowerCase() === 'input';
            const event = form ? 'change' : 'click';
            // Find variant from combined options
            return Bacon.fromEvent(variantOption, event).map(() => {
                const variant = this.product.activeVariant;
                return variant.id;
            });
        });
    }
}
